import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order } from '@hero/schema';
import { useOrderDisplayInfo, useOrderDrawerStore, useOrderDestination } from '@hero/hooks-shared';
import { formatDateMain } from '@hero/core';
import { isInternalShipment, useViewerLeg } from '@hero/web-hooks';
import { CostIcon, VehicleMainIcon, LocationIcon, DateIcon } from '@hero/web-icons';

import { OrderStatusBadge } from '../Status/OrderStatusBadge';
import { ClientDisplay } from '../../Client/ClientDisplay';
import { VehicleName } from '../../Vehicle/VehicleName';
import { Card, CardActionMenu } from '../../../app/Card/Card';
import { RefDisplay } from '../../../app/Ref/RefDisplay';
import { PriceBadge, type PriceBadgeProps } from '../../../app/Price/PriceBadge';
import { Price } from '../../../app/Price';
import { RouteArrowDisplay } from '../../../app/RouteArrowDisplay/RouteArrowDisplay';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/Tooltip';
import type { StatusBadgeConfigType } from '../../../app/Table';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';

type PaymentStatus = 'paid' | 'partial' | 'unpaid';

const PAYMENT_STATUS_COLOR: Record<PaymentStatus, PriceBadgeProps['color']> = {
  paid: 'green',
  partial: 'amber',
  unpaid: 'red',
};

export interface OrderCardProps {
  order: Order;
  displayForUser: 'customer' | 'carrier';
  onViewOrder?: (order: Order) => void;
  onClick?: (order: Order) => void;
  /** Extra actions for the dropdown menu. If omitted, default actions (view order, waybill) are used. Pass `[]` to hide the menu entirely. */
  actions?: ActionButtonGroup[];
  statusConfig?: StatusBadgeConfigType;
  className?: string;
}

export const OrderCard: React.FC<OrderCardProps> = ({
  order,
  displayForUser,
  onViewOrder,
  onClick,
  actions,
  statusConfig,
  className,
}) => {
  const { t } = useTranslation(['c-price', 'm-order']);
  const openOrderDrawer = useOrderDrawerStore(s => s.open);
  const viewerLeg = useViewerLeg(order);
  const { orderInfo, status, visibility, priceUi } = useOrderDisplayInfo({
    order,
    displayForUser,
    viewerLeg,
  });
  const { firstPickup, lastDropoff } = useOrderDestination(order.destinations);

  const amount = priceUi.amount;

  const paymentStatus: PaymentStatus = useMemo(() => {
    if (!amount) return 'unpaid';
    if (amount.remaining <= 0) return 'paid';
    if (amount.paid > 0) return 'partial';
    return 'unpaid';
  }, [amount]);

  const pickupDate = order.dates?.pickup?.from
    ? formatDateMain({ format: 'date_day', date: order.dates.pickup.from })
    : undefined;

  const clientName = !isInternalShipment(order) ? order.client?.name?.trim() : undefined;
  const handleCardClick = () => {
    if (onClick) {
      onClick(order);
    } else if (onViewOrder) {
      onViewOrder(order);
    } else {
      openOrderDrawer(String(order._id));
    }
  };

  return (
    <Card onClick={handleCardClick} className={className}>
      {/* Row 1: Route + Status + Action menu */}
      <div className="flex items-center justify-between gap-3">
        {firstPickup || lastDropoff ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 min-w-0">
            <div className="flex items-center justify-center rounded-md bg-blue-50 p-1.5 flex-shrink-0">
              <LocationIcon className="size-3.5 text-blue-600" />
            </div>
            <RouteArrowDisplay origin={firstPickup} destinations={[lastDropoff]} size="small" />
          </div>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <OrderStatusBadge status={status.currentStatus} statusConfig={statusConfig} />
          {actions && <CardActionMenu actions={actions} />}
        </div>
      </div>

      {/* Row 2: Metadata — ref · vehicle · date */}
      <div className="flex flex-wrap items-center justify-evenly gap-y-1 mt-1">
        <RefDisplay refMain={orderInfo.ref?.main} refShort={orderInfo.ref?.short} />

        {clientName && (
          <>
            <span className="text-gray-200">·</span>
            <ClientDisplay
              clientName={clientName}
              className="text-[10px] py-0.5 px-1.5 max-w-[140px]"
            />
          </>
        )}

        {orderInfo.vehicleType && (
          <>
            <span className="text-gray-200">·</span>
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <VehicleMainIcon className="size-3.5 text-gray-400 flex-shrink-0" />
              <VehicleName vehicle={orderInfo.vehicleType} className="text-gray-600 truncate" />
            </div>
          </>
        )}

        {pickupDate && (
          <>
            <span className="text-gray-200">·</span>
            <div className="flex items-center gap-1.5 text-sm text-gray-600 flex-shrink-0">
              <DateIcon className="size-3.5 text-gray-400" />
              <span>{pickupDate}</span>
            </div>
          </>
        )}
      </div>

      {visibility.showPrice && (
        <div className="flex items-center justify-end mt-1">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PriceBadge
                  price={amount?.total ?? 0}
                  currency={amount?.currency ?? orderInfo.currency}
                  color={
                    displayForUser === 'carrier' ? 'blue' : PAYMENT_STATUS_COLOR[paymentStatus]
                  }
                  icon={<CostIcon className="size-3.5" />}
                  className="cursor-default transition-colors"
                />
              </TooltipTrigger>
              {amount && (
                <TooltipContent side="top" className="p-0">
                  <div className="flex flex-col gap-1 rounded-md p-2.5 text-xs min-w-[140px]">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t('c-price:total')}</span>
                      <span className="font-semibold">
                        <Price amount={amount.total} currency={amount.currency} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t('c-price:paid')}</span>
                      <span className="font-semibold text-emerald-600">
                        <Price amount={amount.paid} currency={amount.currency} />
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t('c-price:remaining')}</span>
                      <span className="font-semibold text-red-600">
                        <Price amount={amount.remaining} currency={amount.currency} />
                      </span>
                    </div>
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </Card>
  );
};
